/**
 * Medusa — „Battle of Legends, Volume One".
 *
 * Pozor: zdroj (unmatched.cards) má pri Medúze žartovné texty so zrkadlami
 * („unless they have a mirror", „If you're playing Titania…"). Implementované
 * je **oficiálne znenie** z rulebooku a kariet: Gaze of Stone „If you won the
 * combat, deal 8 damage to the opposing fighter", A Momentary Glance „Deal 2
 * damage to any one fighter in Medusa's zone", schopnosť „At the start of your
 * turn, you may deal 1 damage to an opposing fighter in Medusa's zone".
 */

import { shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, getMap, heroOf } from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { draw, feint, regroup } from './common.js';
import { askBoost, askMove, enemyFighters, fighterOrNull, opponentDiscards } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const HARPY = 'harpies';

/** Figúrky v Medúzinej zóne (bez nej samej). */
function inMedusaZone(ctx: Ctx, player: PlayerIndex, ids: string[]): string[] {
  const medusa = heroOf(ctx.state, player);
  if (!medusa) return [];
  const map = getMap(ctx);
  return ids.filter((id) => {
    const fighter = fighterOrNull(ctx, id);
    return fighter ? fighter.id !== medusa.id && shareZone(map, medusa.spaceId, fighter.spaceId) : false;
  });
}

/** „Move your fighter up to 3 spaces." */
const dash: CardEffect = {
  after: (e) => {
    if (!e.fighterId) return;
    askMove(e, { fighters: [e.fighterId], max: 3, label: 'Dash: presuň figúrku až o 3 priestory' });
  },
};

/** „If you won the combat, deal 8 damage to the opposing fighter." */
const gazeOfStone: CardEffect = {
  after: (e) => {
    if (e.won && e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 8);
  },
};

/** „Deal 2 damage to any one fighter in Medusa's zone." */
const aMomentaryGlance: CardEffect = {
  scheme: (e) => {
    const targets = inMedusaZone(
      e.ctx,
      e.player,
      e.ctx.state.fighters.map((f) => f.id),
    );
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'A Momentary Glance: 2 poškodenia figúrke v Medúzinej zóne',
      optional: false,
      amount: 2,
      targets,
    });
  },
};

/** „Your opponent discards 1 card." */
const makeOpponentDiscard: CardEffect = {
  after: (e) => opponentDiscards(e, e.opponentPlayer),
};

/** „Move each Harpy up to 3 spaces." */
const houndsOfMightyZeus: CardEffect = {
  after: (e) => {
    for (const harpy of e.ctx.state.fighters.filter((f) => f.owner === e.player && f.defId === HARPY)) {
      askMove(e, { fighters: [harpy.id], max: 3, label: `Presuň ${harpy.name} až o 3 priestory` });
    }
  },
};

/**
 * „Move each of your fighters up to 3 spaces… through opposing fighters.
 * Then, return a defeated Harpy (if any) to any space in Medusa's zone."
 * Návrat ide cez `auto` voľbu — spustí sa až po presunoch, keď je jasné,
 * kde Medusa stojí.
 */
const wingedFrenzy: CardEffect = {
  scheme: (e) => {
    for (const fighter of e.ctx.state.fighters.filter((f) => f.owner === e.player)) {
      askMove(e, {
        fighters: [fighter.id],
        max: 3,
        through: true,
        label: `Presuň ${fighter.name} až o 3 priestory (smie prejsť cez súperove figúrky)`,
      });
    }
    e.ctx.state.pending.push({
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Winged Frenzy: návrat porazenej Harpie',
      optional: false,
      follow: { kind: 'effect', effect: 'medusa.winged-frenzy', data: '' },
    });
  },
  onFollow: (ctx, choice) => {
    const harpy = ctx.state.defeated?.find((f) => f.owner === choice.player && f.defId === HARPY);
    const medusa = heroOf(ctx.state, choice.player);
    if (!harpy || !medusa) return;
    const map = getMap(ctx);
    const spaces = map.spaces
      .filter((s) => s.id === medusa.spaceId || shareZone(map, medusa.spaceId, s.id))
      .filter((s) => !ctx.state.fighters.some((f) => f.spaceId === s.id))
      .map((s) => s.id);
    ctx.state.pending.unshift({
      kind: 'place',
      player: choice.player,
      source: choice.source,
      label: 'Winged Frenzy: vráť porazenú Harpiu na priestor v Medúzinej zóne',
      optional: false,
      fighterId: harpy.id,
      spaces,
    });
  },
};

/** „DURING COMBAT: You may BOOST this attack." */
const secondShot: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Second Shot: smieš boostovať útok kartou z ruky'),
};

export const medusaCards: EffectRegistry = {
  'medusa.dash': dash,
  'medusa.gaze-of-stone': gazeOfStone,
  'medusa.a-momentary-glance': aMomentaryGlance,
  'medusa.hiss-and-slither': makeOpponentDiscard,
  'medusa.the-hounds-of-mighty-zeus': houndsOfMightyZeus,
  'medusa.clutching-claws': makeOpponentDiscard,
  'medusa.winged-frenzy': wingedFrenzy,
  'medusa.second-shot-3': secondShot,
  'medusa.snipe': draw(1),
  'medusa.feint': feint,
  'medusa.regroup': regroup,
};

/** „At the start of your turn, you may deal 1 damage to an opposing fighter in Medusa's zone." */
export const medusaSpecial: HeroSpecial = {
  name: 'Kamenný pohľad',
  startOfTurn: (ctx, player) => {
    const targets = inMedusaZone(ctx, player, enemyFighters(ctx, player));
    askChoice(ctx, {
      kind: 'damage',
      player,
      source: null,
      label: 'Začiatok ťahu: smieš dať 1 poškodenie súperovi v Medúzinej zóne',
      optional: true,
      amount: 1,
      targets,
    });
  },
};
